import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { EMPTY, of, forkJoin, throwError, Observable, Subscription } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ComponentEffects, logCatchError } from '@mhe/reader/common';
import { MediatorUtils } from './mediator-utils';
import { WidgetData, WidgetInstance } from '@mhe/reader/models';
import { ReaderStore } from '@mhe/reader/components/reader/state';
import { WidgetsApiService } from '@mhe/reader/core/reader-api/widgets-api.service';
import {
  SessionDisabledError,
  SessionEndedError,
} from '@mhe/reader/core/utils/reduced-mode-utils';
import { FloatingTtsControlsService, TTSStore } from '@mhe/reader/components/text-to-speech';
import { NavigationStore } from '@mhe/reader/components/navigation';
import * as navigationActions from '@mhe/reader/components/navigation/state/navigation.actions';
import {
  ConfirmationModalComponent,
  ConfirmationModalData,
} from '@mhe/reader/components/modals/confirmation-modal';
import {
  getAllWidgetInstanceIDs,
  getAllWidgets,
  getWidgetsStateByWidgetUuid,
} from '@mhe/reader/global-store/widgets';
import * as widgetsActions from '@mhe/reader/global-store/widgets/widgets.actions';
import { WidgetsRegistryService } from '../core/widgets/widgets-registry.service';
import { widgetsReadAloudTextsReady } from '../components/text-to-speech/state/tts.actions';

@Injectable()
export class WidgetsMediator extends ComponentEffects {
  // used with a local storage item to enable debug logging for widgets
  private readonly debugVerbose: boolean;

  constructor(
    private readonly actions$: Actions,
    private readonly dialog: MatDialog,
    private readonly readerStore: ReaderStore,
    private readonly store: Store,
    private readonly translate: TranslateService,
    private readonly util: MediatorUtils,
    private readonly widgetsApiService: WidgetsApiService,
    private readonly floatingTtsOverlayService: FloatingTtsControlsService,
    private readonly navigationStore: NavigationStore,
    private readonly widgetsRegistryService: WidgetsRegistryService,
    private readonly ttsStore: TTSStore,
  ) {
    super();
    try {
      this.debugVerbose = localStorage.getItem('readerx_widgets_debug') === 'true';
    } catch (e) {
      this.debugVerbose = false;
    }
  }

  private readonly _searchWidgetData$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.widgetDataSearch),
      withLatestFrom(this.util.readerApi$, this.util.requestContext$),
      mergeMap(([{ widgetUuid }, api, userdata]) => {
        // TODO remove these to allow config values to pass through
        // userdata.platform = 'testing';
        // userdata.contextID = 'testing';

        // flag that the request has started (false means requested, but not finished)
        this.store.dispatch(
          widgetsActions.widgetInstanceSetWidgetSearch({
            widgetUuid,
            isRequested: false,
          }),
        );

        // make the api request for an possible existing data
        return this.widgetsApiService
          .searchWidgetData(api, { ...userdata, instanceID: widgetUuid })
          .pipe(
            catchError(() => {
              // when session is disabled or session ends, we still want to complete
              // the search so that the widget iframe loads
              return of(undefined);
            }),
            map((widget) => widget?.[0]),
            tap((widget) => {
              if (widget) {
                // data was returned, update the widget instance
                this.store.dispatch(
                  widgetsActions.widgetInstanceUpdateWidgetData({
                    widgetUuid,
                    widgetData: widget,
                  }),
                );
              }

              // flag that the request has completed (false means requested, but not finished)
              this.store.dispatch(
                widgetsActions.widgetInstanceSetWidgetSearch({
                  widgetUuid,
                  isRequested: true,
                }),
              );
            }),
            logCatchError('_searchWidgetData$'),
          );
      }),
    ),
  );

  private readonly _saveWidgetData$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.widgetDataSave),

      this.util.filterAssignmentReview,

      // not more often than half a second
      debounceTime(500),

      // select the associated widget instance
      mergeMap(({ widgetUuid, dataStringified }) =>
        this.store.pipe(
          select(getWidgetsStateByWidgetUuid, { widgetUuid }),
          take(1),
          map((widgetInstance) => ({
            widgetUuid,
            dataStringified,
            widgetInstance,
          })),
        ),
      ),

      // only when there is a widget instance
      filter(({ widgetUuid, dataStringified, widgetInstance }) =>
        Boolean(widgetInstance),
      ),

      // include api and user data
      withLatestFrom(
        this.util.readerApi$,
        this.util.requestContext$,
        this.util.sessionDisabledConfig$,
      ),

      // do a back end save
      switchMap(
        ([
          { widgetUuid, dataStringified, widgetInstance },
          api,
          userData,
          sessionDisabled,
        ]) => {
          const dataToSave: WidgetData = {
            ...widgetInstance.widgetData,
            ...userData,
          };

          // TODO remove these to allow config values to pass through
          // dataToSave.contextID = 'testing';
          // dataToSave.platform = 'testing';

          // apply the new data
          dataToSave.data = dataStringified;

          // update the timestamp
          const modifiedNow = new Date();
          dataToSave.clientModifiedAt = modifiedNow.toISOString();

          if (!sessionDisabled) {
            // flag to display UI saving chip
            this.store.dispatch(
              widgetsActions.widgetInstanceIsSaving({
                widgetUuid,
                isSaving: 'saving',
              }),
            );
          }

          // make the api request to save the data externally
          return this.widgetsApiService
            .upsertWidgetData(api, { ...dataToSave })
            .pipe(
              catchError(() => {
                // if session is not disabled, show the not-saved UI chip
                if (!sessionDisabled) {
                  this.store.dispatch(
                    widgetsActions.widgetInstanceIsSaving({
                      widgetUuid,
                      isSaving: 'not-saved',
                    }),
                  );
                }

                return EMPTY;
              }),
              tap((widgetDataResponse) => {
                // udpate widget data in the store
                this.store.dispatch(
                  widgetsActions.widgetInstanceUpdateWidgetData({
                    widgetUuid,
                    widgetData: widgetDataResponse,
                  }),
                );

                if (!sessionDisabled) {
                  // flag to dismiss UI saving chip
                  this.store.dispatch(
                    widgetsActions.widgetInstanceIsSaving({
                      widgetUuid,
                      isSaving: 'saved',
                    }),
                  );
                }
              }),
              logCatchError('_saveWidgetData$'),
            );
        },
      ),
    ),
  );

  private confirmationDialog$(
    action,
  ): MatDialogRef<ConfirmationModalComponent, any> {
    const title = this.translate.instant('reset_activities.title');

    let content = this.translate.instant('reset_activities.message_all');

    // change text based on scope value
    if (action.scope === 'exhibit') {
      content = this.translate.instant('reset_activities.message_exhibit');
    }

    const closeText = this.translate.instant('shared.cancel');
    const confirmText = this.translate.instant('shared.ok');

    const data: ConfirmationModalData = {
      title,
      content,
      closeText,
      confirmText,
    };
    return this.dialog.open(ConfirmationModalComponent, {
      data,
      ariaLabelledBy: 'confirmation-modal-h2',
    });
  }

  private readonly _confirmDeleteWidgetData$ = this.effect(() => {
    let scope: 'exhibit' | 'all';
    return this.actions$.pipe(
      ofType(widgetsActions.resetActivitiesConfirm),
      exhaustMap((action) => {
        scope = action.scope;
        return this.confirmationDialog$(action).afterClosed().pipe(
          tap(() => {
            document.querySelector<HTMLElement>(
              'button[data-automation-id="overflow-menu"]',
            )?.focus();
          }),
        );
      }),
      filter((rsp) => Boolean(rsp)),
      tap(() => this.store.dispatch(widgetsActions.resetActivities({ scope }))),
      logCatchError('_confirmDeleteWidgetData$'),
    );
  });

  // TODO UX for when session is disabled?
  private readonly _deleteWidgetData$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.resetActivities),
      withLatestFrom(this.util.readerApi$, this.util.requestContext$),
      mergeMap(([{ scope }, api, userdata]) => {
        // ga event args
        const eventCategory = 'Overflow Menu';
        let eventAction: string;

        switch (scope) {
          case 'exhibit':
            eventAction = 'Reset Page Activities';

            // need to call the delete by instanceID for each widget currently in the store
            return this.store.pipe(
              select(getAllWidgetInstanceIDs),
              take(1),
              mergeMap((allWidgets) => {
                if (allWidgets.length === 0) {
                  return of(1);
                }
                return forkJoin(
                  allWidgets.map((widget) =>
                    this.widgetsApiService.deleteWidgetData(api, {
                      ...userdata,
                      instanceID: widget,
                    }),
                  ),
                );
              }),
              this.util.tapGaEvent({ eventCategory, eventAction }),
              tap(() =>
                this.navigationStore.dispatch(
                  navigationActions.forceRefreshSpineItem(),
                ),
              ),
              catchError(() => {
                this.readerStore.addAlert({
                  alertType: 'alert-warning',
                  translateKey: 'reset_activities.delete_error',
                });
                return EMPTY;
              }),
              logCatchError('_deleteWidgetData$'),
            );

          case 'all':
            eventAction = 'Reset All Activities';

            // just call a single delete api endpiont for everything, does not matter what is in the store
            return this.widgetsApiService
              .deleteWidgetData(api, { ...userdata })
              .pipe(
                catchError((e) =>
                  e instanceof SessionDisabledError ||
                    e instanceof SessionEndedError
                    ? of(1)
                    : throwError(e),
                ),
                this.util.tapGaEvent({ eventCategory, eventAction }),
                tap(() =>
                  this.navigationStore.dispatch(
                    navigationActions.forceRefreshSpineItem(),
                  ),
                ),
                catchError(() => {
                  this.readerStore.addAlert({
                    alertType: 'alert-warning',
                    translateKey: 'reset_activities.delete_error',
                  });
                  return EMPTY;
                }),
                logCatchError('_deleteWidgetData$'),
              );

          default:
          // Unexpected scope provided for resetting activities
        }

        return EMPTY;
      }),
    ),
  );

  private readonly _resetWidgetIframes$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.resetIframes),
      withLatestFrom(this.store.pipe(select(getAllWidgetInstanceIDs))),
      tap((action: any) => {
        action[1].forEach((instanceID) => {
          this.store.dispatch(
            // with each widget in the store, flag that it needs a reset
            // the WidgetManager will detect this and handle the iframe reset
            widgetsActions.widgetInstanceResetIframe({
              widgetUuid: instanceID,
              widgetIframeReset: 'pending',
            }),
          );
        });
      }),
    ),
  );

  private readonly _textToSpeechRequest$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.widgetTextToSpeech),
      exhaustMap((action) => {
        return this.floatingTtsOverlayService.openContextReadspeaker(
          false,
          action.ePubIframe,
          action.textToSpeechRequest,
        );
      }),
      logCatchError('_textToSpeechRequest$'),
    ),
  );

  private readonly _gatherWidgetReadAloudTexts$ = this.effect(() =>
    this.actions$.pipe(
      ofType(widgetsActions.widgetReadAloudGatherTexts),
      tap(() => {
        if (this.debugVerbose) {
          console.log('{WIDGETS MEDIATOR} gathering read aloud texts from widgets');
        }
      }),
      withLatestFrom(this.store.pipe(select(getAllWidgets))),
      tap(([action, widgetInstances]) => {
        if (this.debugVerbose) {
          console.log('{WIDGETS MEDIATOR} widgetInstances:', widgetInstances);
        }

        // use the registry service to get the WidgetManager instances
        const widgetManagers = widgetInstances
          .map((wi) =>
            this.widgetsRegistryService.get(wi.widgetUuid),
          )
          .filter((wm): wm is NonNullable<typeof wm> => Boolean(wm));

        // for each WidgetManager, ask for read aloud text and wait for the response
        const widgetManagerPromises = widgetManagers.map(async(wm) => {
          // remove lazy loading to ensure iframe begins loading if not already
          wm.widgetIframe.removeAttribute('loading');

          return await new Promise<boolean>((resolve) => {
            // get the observable model from the store
            const wi = this.getWidgetInstance(wm.widgetUuid);

            // subscribe to the observable widget instance
            // eslint-disable-next-line prefer-const
            let sub: Subscription;
            // eslint-disable-next-line prefer-const
            let loadingSub: Subscription | undefined;
            // eslint-disable-next-line prefer-const
            let acmeReadySub: Subscription | undefined;
            let timeoutId: ReturnType<typeof setTimeout>;

            const startTimeout = (): void => {
              // clear any existing timeout
              if (timeoutId) {
                clearTimeout(timeoutId);
              }

              // if no value ever comes, resolve as false after a timeout
              // this could be an iframe which is not a widget or is very slow to load and respond
              timeoutId = setTimeout(() => {
                if (this.debugVerbose) {
                  console.log('{WIDGETS MEDIATOR}', wm.widgetUuid, ' read aloud text request timed out');
                }
                sub.unsubscribe();
                loadingSub?.unsubscribe();
                acmeReadySub?.unsubscribe();
                resolve(false);
              }, 7500);
            };

            // watch for read aloud text to be received
            sub = wi.subscribe((widgetInstance) => {
              if (!widgetInstance) {
                return;
              }

              // check if there is text received
              if (typeof widgetInstance.readAloudTextReceived === 'string') {
                if (this.debugVerbose) {
                  console.log('{WIDGETS MEDIATOR}', widgetInstance.widgetUuid,
                    ' read aloud text received:', widgetInstance.readAloudTextReceived);
                }
                clearTimeout(timeoutId);
                sub?.unsubscribe();
                loadingSub?.unsubscribe();
                acmeReadySub?.unsubscribe();
                resolve(true);
              }
            });

            // watch for acme shared ready, and reset the timeout (only once)
            acmeReadySub = wi
              .pipe(
                filter((widgetInstance) => Boolean(widgetInstance?.widgetAcmeSharedReady)),
                take(1),
              )
              .subscribe((widgetInstance) => {
                if (this.debugVerbose) {
                  console.log('{WIDGETS MEDIATOR}',
                    widgetInstance.widgetUuid, ' resetting timer, widget acme shared ready');
                }
                acmeReadySub?.unsubscribe();
                // reset timeout when acme shared is ready
                startTimeout();
              });

            // check current state before asking for text
            wi.pipe(take(1)).subscribe((widgetInstance) => {
              // if text already exists, no need to request it
              if (widgetInstance && typeof widgetInstance.readAloudTextReceived === 'string') {
                if (this.debugVerbose) {
                  console.log('{WIDGETS MEDIATOR}', widgetInstance.widgetUuid,
                    ' read aloud text already exists:', widgetInstance.readAloudTextReceived);
                }
                clearTimeout(timeoutId);
                sub?.unsubscribe();
                acmeReadySub?.unsubscribe();
                resolve(true);
                return;
              }

              // if widget is not loaded yet, wait for it to load before asking for text
              if (!widgetInstance?.widgetLoadingComplete) {
                // watch for loading complete
                loadingSub = wi.subscribe((wi) => {
                  if (wi?.widgetLoadingComplete) {
                    if (this.debugVerbose) {
                      console.log('{WIDGETS MEDIATOR}', wi.widgetUuid,
                        ' widget loaded, now requesting read aloud text');
                    }
                    loadingSub?.unsubscribe();

                    // tell the widget to ask for read aloud text
                    wm.readAloud_askForText();

                    // reset the timeout now that widget is loaded and we've asked for text
                    startTimeout();
                  }
                });

                // start initial timeout
                startTimeout();
                return;
              }

              // widget is loaded, tell it to ask for read aloud text
              wm.readAloud_askForText();

              // start timeout
              startTimeout();
            });
          });
        });

        Promise
          .all(widgetManagerPromises)
          .then((results) => {
            if (this.debugVerbose) {
              console.log('{WIDGETS MEDIATOR} all read aloud text gathering completed:', results);
            }
            // dipatch actions to indicate all texts are ready

            const hasWidgets = Array.isArray(results) && results.length > 0;

            // widget mediator action
            this.store.dispatch(widgetsActions.widgetReadAloudTextsReady({ hasWidgets }));

            // tts mediator action
            // signals that read aloud can proceed
            this.ttsStore.dispatch(widgetsReadAloudTextsReady({ hasWidgets }));
          })
          .catch((error) => {
            // TODO any error handling to display in the UI?
            if (this.debugVerbose) {
              console.error('{WIDGETS MEDIATOR} error gathering read aloud texts from widgets:', error);
            }
          });
      }),
      logCatchError('_gatherWidgetReadAloudTexts$'),
    ),
  );

  public getWidgetInstance(widgetUuid: string): Observable<WidgetInstance> {
    return this.store.pipe(select(getWidgetsStateByWidgetUuid, { widgetUuid }));
  }
}
